import BottomSheetHeader from '@components/BottomSheetHeader';
import Button from '@components/Button';
import Column from '@components/Column';
import Map from '@components/Map';
import Row from '@components/Row';
import Text from '@components/Text';
import TextUnderline from '@components/TextUnderline';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import useCopyShopAddress from '@hooks/shop/useCopyShopAddress';
import useShop from '@hooks/shop/useShop';
import useAppCustom from '@hooks/useAppCustom';
import Copy from '@icons/Copy';
import PhoneFill from '@icons/phone/PhoneFill';
import { useFlow } from '@router/stackflow';
import { BottomSheet } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import NativeUtil from '@utils/NativeUtil';
import { useMemo } from 'react';

import styles from './styles.module.scss';
import WarningIcon from './WarningIcon';

interface Params {
    shopId: string;
    lat?: number;
    lng?: number;
}

const ShopLocationBottomSheet:ActivityComponentType<Params> = ({ params }:{params: Params}) => {
  const {
    shopId, lat, lng,
  } = params;
  const shop = useShop(shopId);
  const { pop, push } = useFlow();
  const copyAddress = useCopyShopAddress(shop?.address);
  const nameWords : string[] = useMemo(() => shop?.name.match(new RegExp(`.{1,${16}}`, 'g')) || [], [shop]);
  const handleOrderButtonClick = () => {
    pop();
    push('ItemsActivity', { shopId, type: 'ALL' });
  };
  const { usePhoneNumber } = useAppCustom().shop;
  return (
        <BottomSheet>
            <BottomSheetHeader/>
            <Column fullWidth align='center' spacing={20}>
                {shop && (
                    <Map center={{ lat: lat!, lng: lng! }}
                         width='100%'
                         height={218}
                         markers={[
                           {
                             lat: lat!, lng: lng!, name: shop.name, id: shop.id, isMyShop: false,
                           },
                         ]} />
                )}
                <Column align='center' className={styles.contents}>
                    {nameWords.map((word, index) => (
                        <TextUnderline key={index}>
                            <Text size='3xl' fontWeight='bold' className={styles.name}>
                                {word}
                            </Text>
                        </TextUnderline>
                    ))}
                    <Text size='3xl'>에서 주문하시겠습니까?</Text>
                </Column>
                <Column align='center'>
                    <Row spacing={8} onClick={copyAddress}
                         className={styles.addressContainer} >
                        <Text color={colors.gray6} size='lg' align='center' className={styles.address}>
                            {shop?.address}
                        </Text>
                        <Copy color={colors.gray6} size={16}/>
                    </Row>
                    {(shop?.phoneNumber && usePhoneNumber) && (
                        <Row spacing={8}
                             onClick={() => NativeUtil.postMessage('openExternalLink', `tel:${shop.phoneNumber}`)}>
                            <Text color={colors.gray6} size='lg'>
                                {shop.phoneNumber}
                            </Text>
                            <PhoneFill color={colors.gray6} size={16}/>
                        </Row>
                    )}
                </Column>
                <Column className={styles.footer} fullWidth
                        spacing={30} >
                    <Column align='center' className={styles.warning}
                            onClick={pop} >
                        <Row>
                            <WarningIcon/>
                            <Text>선택하신 해당 매장이 맞나요?</Text>
                        </Row>
                        <Text>주문 후 주문 변경 또는 취소가 불가합니다.</Text>
                    </Column>
                    <Row fullWidth spacing={8}>
                        <Button variant='secondary' onClick={pop} css={css`flex: 1; padding: 0;`}>
                            다른 매장 선택
                        </Button>
                        <Button css={css`flex: 1; padding: 0;`}
                                disabled={shop?.orderStatus !== 'OPEN'}
                                onClick={handleOrderButtonClick} >
                            {shop?.orderStatus === 'OPEN' ? '주문하기' : '준비중입니다'}
                        </Button>
                    </Row>
                </Column>
            </Column>
        </BottomSheet>
  );
};

export default ShopLocationBottomSheet;
